/**
 * Extract a text snippet from the first 3 pages of a PDF for IRL matching.
 * Uses pdfjs-dist (already a dep). If text extraction yields very little
 * (likely a scan), the caller can fall back to filename-only matching —
 * we don't run OCR here in v1.
 */
import * as pdfjsLib from "pdfjs-dist/legacy/build/pdf.mjs";

// pdfjs-dist v5 does parsing in a Web Worker. Without a workerSrc, the
// first call to getDocument throws synchronously. Pin to the same version
// we have installed so the worker matches the API. CDN works in a
// browser-only build like CRA without webpack-config gymnastics.
if (typeof window !== "undefined" && !pdfjsLib.GlobalWorkerOptions.workerSrc) {
  const version = (pdfjsLib as any).version || "5.5.207";
  pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdn.jsdelivr.net/npm/pdfjs-dist@${version}/legacy/build/pdf.worker.min.mjs`;
}

const MAX_PAGES = 3;
const MAX_CHARS = 4000;

export async function extractPdfPreview(file: File): Promise<string> {
  try {
    const buffer = await file.arrayBuffer();
    const data = new Uint8Array(buffer);
    // isEvalSupported:false — GHSA-hq66-cqwq-w95j (arbitrary JS execution from a
    // malicious PDF). These PDFs come from the VDR, i.e. from outside.
    const doc = await pdfjsLib.getDocument({ data, isEvalSupported: false, useSystemFonts: true } as any).promise;

    const pageCount = Math.min(MAX_PAGES, doc.numPages);
    const chunks: string[] = [];

    for (let p = 1; p <= pageCount; p++) {
      try {
        const page = await doc.getPage(p);
        const content = await page.getTextContent();
        const text = (content.items as any[])
          .map((it: any) => (typeof it.str === "string" ? it.str : ""))
          .join(" ")
          .replace(/\s+/g, " ")
          .trim();
        if (text) chunks.push(`[Page ${p}]\n${text}`);
      } catch (pageErr) {
        console.warn(`[irl-match] PDF page ${p} read failed:`, pageErr);
      }

      if (chunks.join("\n\n").length > MAX_CHARS) break;
    }

    return chunks.join("\n\n").slice(0, MAX_CHARS);
  } catch (err) {
    console.warn(`[irl-match] PDF extraction failed for "${file.name}":`, err);
    return "";
  }
}
